/* ==========================================
   TA Hero Slider
   Torre Arka
========================================== */

(() => {
  const HERO_SELECTOR = '[data-ta-hero]';

  class TAHeroSlider {
    constructor(element) {
      if (!element || element.dataset.taHeroReady === 'true') return;

      element.dataset.taHeroReady = 'true';

      this.hero = element;
      this.slides = Array.from(
        element.querySelectorAll('[data-ta-hero-slide]')
      );

      this.indicators = Array.from(
        element.querySelectorAll('[data-ta-hero-indicator]')
      );

      this.previousButton = element.querySelector('[data-ta-hero-prev]');
      this.nextButton = element.querySelector('[data-ta-hero-next]');
      this.viewport = element.querySelector('[data-ta-hero-viewport]');

      this.autoplay = element.dataset.autoplay === 'true';
      this.pauseOnHover = element.dataset.pauseHover === 'true';
      this.delay = Number(element.dataset.delay) || 7000;

      this.currentIndex = Math.max(
        0,
        this.slides.findIndex((slide) =>
          slide.classList.contains('is-active')
        )
      );

      this.timer = null;
      this.isPaused = false;
      this.touchStartX = 0;
      this.touchEndX = 0;

      this.onPrevious = () => this.show(this.currentIndex - 1, true);
      this.onNext = () => this.show(this.currentIndex + 1, true);

      this.init();
    }

    init() {
      if (this.slides.length <= 1) return;

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);

      this.indicators.forEach((indicator) => {
        indicator.addEventListener('click', () => {
          const index = Number(indicator.dataset.slideIndex);
          this.show(index, true);
        });
      });

      if (this.pauseOnHover) {
        this.hero.addEventListener('mouseenter', () => this.pause());
        this.hero.addEventListener('mouseleave', () => this.resume());
      }

      this.hero.addEventListener('focusin', () => this.pause());

      this.hero.addEventListener('focusout', (event) => {
        if (!this.hero.contains(event.relatedTarget)) {
          this.resume();
        }
      });

      this.viewport?.addEventListener(
        'touchstart',
        (event) => {
          this.touchStartX = event.changedTouches[0].screenX;
        },
        { passive: true }
      );

      this.viewport?.addEventListener(
        'touchend',
        (event) => {
          this.touchEndX = event.changedTouches[0].screenX;
          this.handleSwipe();
        },
        { passive: true }
      );

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pause();
        } else {
          this.resume();
        }
      });

      this.restartAutoplay();
    }

    normalizeIndex(index) {
      if (index < 0) return this.slides.length - 1;
      if (index >= this.slides.length) return 0;
      return index;
    }

    show(index, userInitiated = false) {
      const nextIndex = this.normalizeIndex(index);

      if (nextIndex === this.currentIndex) {
        if (userInitiated) this.restartAutoplay();
        return;
      }

      const currentSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[nextIndex];

      currentSlide.classList.add('is-leaving');
      currentSlide.classList.remove('is-active');
      currentSlide.setAttribute('aria-hidden', 'true');

      nextSlide.classList.add('is-active');
      nextSlide.setAttribute('aria-hidden', 'false');

      window.setTimeout(() => {
        currentSlide.classList.remove('is-leaving');
      }, 750);

      this.indicators.forEach((indicator, indicatorIndex) => {
        const isActive = indicatorIndex === nextIndex;

        indicator.classList.toggle('is-active', isActive);
        indicator.setAttribute(
          'aria-current',
          isActive ? 'true' : 'false'
        );

        const progress = indicator.querySelector(
          '.ta-hero-slider__indicator-progress'
        );

        if (progress) {
          progress.style.animation = 'none';
          progress.offsetHeight;
          progress.style.animation = '';
        }
      });

      this.currentIndex = nextIndex;

      if (userInitiated) {
        this.restartAutoplay();
      }
    }

    restartAutoplay() {
      window.clearTimeout(this.timer);

      if (
        !this.autoplay ||
        this.isPaused ||
        this.slides.length <= 1 ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        return;
      }

      this.timer = window.setTimeout(() => {
        this.show(this.currentIndex + 1);
        this.restartAutoplay();
      }, this.delay);
    }

    pause() {
      if (!this.autoplay) return;

      this.isPaused = true;
      this.hero.classList.add('is-paused');
      window.clearTimeout(this.timer);
    }

    resume() {
      if (!this.autoplay) return;

      this.isPaused = false;
      this.hero.classList.remove('is-paused');
      this.restartAutoplay();
    }

    handleSwipe() {
      const distance = this.touchStartX - this.touchEndX;

      if (Math.abs(distance) < 45) return;

      if (distance > 0) {
        this.show(this.currentIndex + 1, true);
      } else {
        this.show(this.currentIndex - 1, true);
      }
    }

    selectBlock(blockId) {
      const index = this.slides.findIndex(
        (slide) => slide.id === `TAHeroSlide-${blockId}`
      );

      if (index >= 0) {
        this.pause();
        this.show(index, false);
      }
    }
  }

  function initialize(scope = document) {
    scope.querySelectorAll(HERO_SELECTOR).forEach((hero) => {
      if (!hero.taHeroInstance) {
        hero.taHeroInstance = new TAHeroSlider(hero);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initialize();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const hero = event.target.closest(HERO_SELECTOR);

    if (!hero?.taHeroInstance) return;

    hero.taHeroInstance.selectBlock(event.detail.blockId);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const hero = event.target.closest(HERO_SELECTOR);
    hero?.taHeroInstance?.resume();
  });
})();